<!--
  @component SecurityRoleSync — sets SecurityIO's role from the authoritative
  server answer (security.whoami) so GuardIO gates accurately app-wide. Mounted
  ambiently inside the security tree. Renders children.

  Dispatches whoami on mount and RETRIES until a valid role comes back, rather
  than gating on `dep.effecting`. `dep.dispatch` queues before the socket opens
  and resolves once connected, so the only failure modes are the first call
  racing the WS open (transient) — a short retry loop covers that. Gating on
  `dep.effecting` proved unreliable: it could lag/never flip, leaving the role
  stuck at the safe default ("viewer") even though every other dispatch worked.
-->
<script lang="ts">
  import type { Snippet } from "svelte";
  import { useSecurityIO } from "./SecurityIO.svelte";
  import { useDeploymentIO } from "../deployment/DeploymentIO.svelte";
  import type { RoleName } from "./security-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const sec = useSecurityIO();
  const dep = useDeploymentIO();

  const ROLES: readonly RoleName[] = ["viewer", "deployer", "operator", "auditor", "owner"];
  const MAX_ATTEMPTS = 10;
  const RETRY_MS = 600;
  let started = false;

  $effect(() => {
    if (started) return;
    started = true;
    void (async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          const r = (await dep.dispatch("security.whoami")) as { role?: string } | null;
          const role = r?.role;
          if (typeof role === "string" && (ROLES as readonly string[]).includes(role)) {
            sec.setRole(role as RoleName);
            return; // got the authoritative role — done
          }
        } catch {
          /* not connected yet / transient — fall through to retry */
        }
        await new Promise((res) => setTimeout(res, RETRY_MS));
      }
      // Exhausted retries — keep the safe default (viewer); the server
      // floor stays authoritative regardless of what the UI shows.
    })();
  });
</script>

{@render children?.()}
